import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography, Paper, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Card, CardContent, Grid, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, TextField, IconButton
} from '@mui/material';
import Papa from 'papaparse';
import {
    FileDownload as FileDownloadIcon,
    FileUpload as FileUploadIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useSnackbar } from '../context/SnackbarContext';
import api from '../api/axiosInstance';

export default function AwardsData() {
    const showSnackbar = useSnackbar();
    const [schema, setSchema] = useState([]);
    const [dbAwards, setDbAwards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});

    // const fetchData = useCallback(async () => {
    //     setLoading(true);
    //     try {
    //         const [settingsRes, awardsRes] = await Promise.all([
    //             fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
    //                 headers: { 'Authorization': `Bearer ${token}` }
    //             }),
    //             fetch(`${process.env.REACT_APP_API_URL}/api/awards`, {
    //                 headers: { 'Authorization': `Bearer ${token}` }
    //             })
    //         ]);

    //         const settingsData = await settingsRes.json();
    //         const awardsData = await awardsRes.json();

    //         // Handle Schema
    //         if (settingsData.award_import_schema) {
    //             const parsedSchema = typeof settingsData.award_import_schema === 'string'
    //                 ? JSON.parse(settingsData.award_import_schema)
    //                 : settingsData.award_import_schema;
    //             setSchema(parsedSchema);
    //         }

    //         // Handle Data
    //         const flattenedAwards = awardsData.map(a => ({
    //             db_id: a.id,
    //             ...a.payload
    //         }));
    //         setDbAwards(flattenedAwards);
    //     } catch (err) {
    //         showSnackbar('Error loading awards data', 'error');
    //     } finally {
    //         setLoading(false);
    //     }
    // }, [token, showSnackbar]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Axios returns the data directly in the 'data' property
            const [settingsRes, awardsRes] = await Promise.all([
                api.get('/settings'),
                api.get('/awards')
            ]);

            const settingsData = settingsRes.data;
            const awardsData = awardsRes.data;

            // Handle Schema
            if (settingsData.award_import_schema) {
                const parsedSchema = typeof settingsData.award_import_schema === 'string'
                    ? JSON.parse(settingsData.award_import_schema)
                    : settingsData.award_import_schema;
                setSchema(parsedSchema);
            }

            // Handle Data mapping
            const flattenedAwards = awardsData.map(a => ({
                db_id: a.id,
                ...a.payload
            }));
            setDbAwards(flattenedAwards);
        } catch (err) {
            showSnackbar('Error loading awards data', 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]); // Removed 'token' from dependencies


    useEffect(() => { fetchData(); }, [fetchData]);

    const startEdit = (award) => { setEditingId(award.db_id); setEditForm(award); };
    const cancelEdit = () => { setEditingId(null); setEditForm({}); };
    const handleEditChange = (id, value) => { setEditForm(prev => ({ ...prev, [id]: value })); };

    // const saveEdit = async (db_id) => {
    //     try {
    //         const { db_id: _, ...payloadOnly } = editForm;
    //         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/awards/${db_id}`, {
    //             method: 'PUT',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'Authorization': `Bearer ${token}`
    //             },
    //             body: JSON.stringify({ payload: payloadOnly })
    //         });

    //         if (res.ok) {
    //             showSnackbar('Award updated', 'success');
    //             setEditingId(null);
    //             fetchData();
    //         }
    //     } catch (err) {
    //         showSnackbar('Failed to update award', 'error');
    //     }
    // };

    const saveEdit = async (db_id) => {
        try {
            const { db_id: _, ...payloadOnly } = editForm;
            await api.put(`/awards/${db_id}`, { payload: payloadOnly });

            showSnackbar('Award updated', 'success');
            setEditingId(null);
            fetchData();
        } catch (err) {
            showSnackbar(err.response?.data?.error || 'Failed to update award', 'error');
        }
    };

    const downloadTemplate = () => {
        if (!schema.length) return showSnackbar('No award schema defined', 'warning');
        const headers = schema.map(col => col.label);
        const csv = Papa.unparse({ fields: headers, data: [] }, { quotes: true });
        const blob = new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Awards_Template_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const mappedRows = results.data.map(row => {
                    const obj = {};
                    schema.forEach(config => { obj[config.id] = row[config.label]; });
                    return obj;
                });
                setPreviewData(mappedRows);
            }
        });
    };

    const handleImport = async () => {
        setUploading(true);
        try {
            await api.post('/awards/import', { awards: previewData });

            showSnackbar('Awards imported!', 'success');
            setPreviewData(null);
            fetchData();
        } catch (err) {
            showSnackbar(err.response?.data?.error || 'Import failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    if (loading && dbAwards.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                <CircularProgress size={60} thickness={4} />
                <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading Award Data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            p: 3,
            flexDirection: 'column',
            // overflow: 'hidden'
        }}>

            {/* Header Section (Non-shrinking) */}
            <Box sx={{
                flexShrink: 0,
                mb: 3,
            }}>
                <Typography variant="h4" gutterBottom>Award Management</Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Manage available awards, categories, and criteria.
                </Typography>

                <Grid container spacing={3}>
                    <Grid item size={5} md={6}>
                        <Card sx={{ height: '100%', boxShadow: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>1. Get CSV Template</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Download a template with your current column configuration.
                                </Typography>
                                <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={downloadTemplate} disabled={schema.length === 0}>
                                    Download Template
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item size={5} md={6}>
                        <Card sx={{ height: '100%', boxShadow: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>2. Upload Awards</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Upload your populated CSV file to update the database.
                                </Typography>
                                <Button variant="contained" component="label" startIcon={<FileUploadIcon />}>
                                    Upload CSV File
                                    <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Scrollable Area */}
            <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {previewData && (
                    <Paper sx={{ p: 3, border: '2px solid #4caf50', flexShrink: 0, minHeight: 100 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" color="success.main">Preview: New Import ({previewData.length})</Typography>
                            <Button variant="contained" color="success" onClick={() => setOpenConfirm(true)}>Confirm Replace</Button>
                        </Box>
                        <TableContainer sx={{ maxHeight: 150 }}><Table size="small">
                            <TableHead><TableRow>{schema.map(c => <TableCell key={c.id}>{c.label}</TableCell>)}</TableRow></TableHead>
                            <TableBody>{previewData.map((row, i) => (
                                <TableRow key={i}>{schema.map(c => <TableCell key={c.id}>{row[c.id]}</TableCell>)}</TableRow>
                            ))}</TableBody>
                        </Table></TableContainer>
                    </Paper>
                )}

                <Paper sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: 3,
                    minHeight: 200
                }}>
                    <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white', flexShrink: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Available Awards ({dbAwards.length})</Typography>
                    </Box>
                    <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', width: 80, zIndex: 3 }}>Actions</TableCell>
                                    {schema.map(col => <TableCell key={col.id} sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>{col.label}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dbAwards.map((award) => (
                                    <TableRow key={award.db_id} hover>
                                        <TableCell>
                                            {editingId === award.db_id ? (
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={() => saveEdit(award.db_id)} color="success"><SaveIcon fontSize="small" /></IconButton>
                                                    <IconButton size="small" onClick={cancelEdit} color="error"><CloseIcon fontSize="small" /></IconButton>
                                                </Box>
                                            ) : (
                                                <IconButton size="small" onClick={() => startEdit(award)} color="primary"><EditIcon fontSize="small" /></IconButton>
                                            )}
                                        </TableCell>
                                        {schema.map(col => (
                                            <TableCell key={col.id}>
                                                {editingId === award.db_id ? (
                                                    <TextField variant="standard" size="small" value={editForm[col.id] || ''} onChange={(e) => handleEditChange(col.id, e.target.value)} fullWidth />
                                                ) : award[col.id]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Replace Award Data?</DialogTitle>
                <DialogContent><DialogContentText>This wipes current awards and replaces them with the CSV data.</DialogContentText></DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={() => { setOpenConfirm(false); handleImport(); }} disabled={uploading} color="error">Replace</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}